import { useEffect, useState, useCallback } from "react";
import { Star, Loader2, Pencil, Trash2, Reply } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

export function StarsDisplay({ value = 0, size = "h-3.5 w-3.5" }) {
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 stroke-amber-400" : "stroke-zinc-300"}`} />
      ))}
    </span>
  );
}

export function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(i)}
          data-testid={`review-star-${i}`}
          className="p-0.5"
        >
          <Star className={`h-6 w-6 transition-colors ${i <= (hover || value) ? "fill-amber-400 stroke-amber-400" : "stroke-zinc-300"}`} />
        </button>
      ))}
    </div>
  );
}

export function ReviewForm({ invoiceId, supplierName, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/review`, {
        rating, comment: comment.trim() || null,
      });
      toast.success(`Thanks — ${supplierName || "the supplier"} rated ${rating}/5`);
      onDone?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="review-form">
      <StarPicker value={rating} onChange={setRating} />
      <Textarea
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="How did the job go? (optional — visible to other yachts)"
        data-testid="review-comment"
      />
      <Button size="sm" onClick={submit} disabled={busy || !rating} className="bg-ink hover:bg-black" data-testid="review-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit review"}
      </Button>
    </div>
  );
}

/* Task details "Review" row: leave / edit / delete a review for the task's
   supplier (or crew assignee) once the task is completed. */
function TaskReviewForm({ taskId, initial, onDone, onCancel }) {
  const [rating, setRating] = useState(initial?.rating || 0);
  const [comment, setComment] = useState(initial?.comment || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { rating, comment: comment.trim() || null };
      const { data } = initial
        ? await api.patch(`/maintenance/${taskId}/review`, payload)
        : await api.post(`/maintenance/${taskId}/review`, payload);
      toast.success(initial ? "Review updated" : "Review saved");
      onDone?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="task-review-form">
      <StarPicker value={rating} onChange={setRating} />
      <Textarea
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="How did the job go? (optional — visible to other yachts)"
        data-testid="task-review-comment"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !rating} className="bg-ink hover:bg-black" data-testid="task-review-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (initial ? "Save review" : "Submit review")}
        </Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid="task-review-cancel">Cancel</Button>}
      </div>
    </div>
  );
}

export function TaskReviewBlock({ taskId, canReview }) {
  const [state, setState] = useState({ loading: true, review: null });
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/maintenance/${taskId}/review`);
      setState({ loading: false, review: data.review });
    } catch { setState({ loading: false, review: null }); }
  }, [taskId]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/maintenance/${taskId}/review`);
      toast.success("Review deleted");
      setConfirmDel(false);
      setState({ loading: false, review: null });
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (state.loading) return <span className="text-zinc-400 text-xs">…</span>;
  const r = state.review;
  if (r && editing) {
    return (
      <TaskReviewForm
        taskId={taskId}
        initial={r}
        onDone={(fresh) => { setEditing(false); setState({ loading: false, review: fresh }); }}
        onCancel={() => setEditing(false)}
      />
    );
  }
  if (r) {
    return (
      <div data-testid="task-review-given">
        <div className="flex items-center gap-2 flex-wrap">
          <StarsDisplay value={r.rating} />
          <span className="text-xs text-zinc-500">by {r.reviewer_name || "crew"} · {(r.updated_at || r.created_at || "").slice(0, 10)}</span>
        </div>
        {r.comment && <p className="text-sm text-zinc-700 mt-0.5">{r.comment}</p>}
        <ReviewReplyDisplay reply={r.reply} supplierName={r.supplier_name} />
        {canReview && (
          <div className="mt-1 flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditing(true)} data-testid="task-review-edit">
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
            {confirmDel ? (
              <>
                <Button size="sm" variant="destructive" className="h-7 px-2 text-xs" onClick={remove} data-testid="task-review-delete-confirm">Confirm delete</Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirmDel(false)} data-testid="task-review-delete-cancel">Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirmDel(true)} data-testid="task-review-delete">
                <Trash2 className="h-3 w-3 mr-1" /> Delete
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
  if (!canReview) return <span className="text-zinc-400 text-sm">—</span>;
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setOpen(true)} data-testid="task-review-open">
        <Star className="h-3 w-3 mr-1 stroke-amber-400" /> Leave review
      </Button>
    );
  }
  return (
    <TaskReviewForm
      taskId={taskId}
      onDone={(fresh) => { setOpen(false); setState({ loading: false, review: fresh }); }}
      onCancel={() => setOpen(false)}
    />
  );
}

/* Supplier hub: one-time public reply under a received review. */
export function ReviewReplyForm({ review, onReplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  if (review.reply) return <ReviewReplyDisplay reply={review.reply} supplierName={review.supplier_name} />;
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs mt-2" onClick={() => setOpen(true)} data-testid={`review-reply-open-${review.id}`}>
        <Reply className="h-3 w-3 mr-1" /> Reply
      </Button>
    );
  }
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reviews/${review.id}/reply`, { text: text.trim() });
      toast.success("Reply posted");
      setOpen(false);
      onReplied?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2" data-testid={`review-reply-form-${review.id}`}>
      <Textarea
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Your public reply — visible to all yachts. You can reply once."
        data-testid="review-reply-text"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !text.trim()} className="bg-ink hover:bg-black" data-testid="review-reply-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post reply"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="review-reply-cancel">Cancel</Button>
      </div>
    </div>
  );
}

/* Owner invoice detail block: prompts for a review once payment is confirmed,
   or shows the review that was already given. */
export function InvoiceReviewBlock({ invoiceId, supplierName, canReview }) {
  const [state, setState] = useState({ loading: true, review: null });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${invoiceId}/review`);
      setState({ loading: false, review: data.review });
    } catch { setState({ loading: false, review: null }); }
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  if (state.loading) return null;
  if (state.review) {
    const r = state.review;
    return (
      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="invoice-review-given">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Your review</div>
        <div className="flex items-center gap-2">
          <StarsDisplay value={r.rating} />
          <span className="text-xs text-zinc-500">
            by {r.reviewer_name || r.created_by_name || "crew"} · {(r.created_at || "").slice(0, 10)}
          </span>
        </div>
        {r.comment && <p className="text-sm text-zinc-700 mt-1">{r.comment}</p>}
        <ReviewReplyDisplay reply={r.reply} supplierName={r.supplier_name} />
      </div>
    );
  }
  if (!canReview) return null;
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="invoice-review-prompt">
      <div className="text-xs text-amber-800 mb-2">
        Payment confirmed — how was {supplierName || "this supplier"}? Your rating helps other yachts find great suppliers.
      </div>
      <ReviewForm invoiceId={invoiceId} supplierName={supplierName} onDone={load} />
    </div>
  );
}

/* Network-card dialog: rate a supplier for a specific completed job. */
export function ReviewDialog({ pending, onClose, onDone }) {
  if (!pending) return null;
  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="supplier-review-dialog">
        <DialogHeader>
          <DialogTitle>Rate {pending.supplier_name || "this supplier"}</DialogTitle>
          <DialogDescription>
            {pending.task_title ? <>For the completed job “{pending.task_title}”.</> : "For the completed job."} Your rating and comment are visible to other yachts.
          </DialogDescription>
        </DialogHeader>
        <ReviewForm
          invoiceId={pending.invoice_id}
          supplierName={pending.supplier_name}
          onDone={(r) => { onDone?.(r); onClose(); }}
        />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="review-dialog-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewReplyDisplay({ reply, supplierName }) {
  if (!reply) return null;
  return (
    <div className="mt-2 rounded-md border-l-2 border-zinc-300 bg-zinc-50 px-3 py-2" data-testid="review-reply">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        Reply from {reply.by_name || supplierName || "the supplier"} · {(reply.created_at || "").slice(0, 10)}
      </div>
      <p className="text-sm text-zinc-700 mt-0.5 whitespace-pre-wrap">{reply.text}</p>
    </div>
  );
}

export function ReviewList({ reviews, emptyText = "No reviews yet." }) {
  if (!reviews?.length) return <p className="text-sm text-zinc-500" data-testid="reviews-empty">{emptyText}</p>;
  return (
    <div className="space-y-3" data-testid="reviews-list">
      {reviews.map((r) => (
        <div key={r.id} className="rounded-lg border border-zinc-200 p-3" data-testid={`review-${r.id}`}>
          <div className="flex items-center justify-between gap-2">
            <StarsDisplay value={r.rating} />
            <span className="text-[11px] text-zinc-400">{(r.created_at || "").slice(0, 10)}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {r.yacht_name || "A yacht"}{r.task_title ? <> · {r.task_title}</> : null}
          </div>
          {r.comment && <p className="text-sm text-zinc-700 mt-1">{r.comment}</p>}
          <ReviewReplyDisplay reply={r.reply} supplierName={r.supplier_name} />
        </div>
      ))}
    </div>
  );
}
